import React, { useState, useEffect, useCallback } from 'react';
import { IconButton, Tooltip, Slider } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import DownloadIcon from '@mui/icons-material/Download';
import { useTranslation } from 'react-i18next';
import XtramModal, { XtramButton, xtramStyles } from '../../Widgets/XtramModal/XtramModal';

const IMAGE_EXT = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'];
const PDF_EXT = ['.pdf'];

const getFileType = (urlOrFileName) => {
    if (!urlOrFileName) return 'unknown';
    const str = typeof urlOrFileName === 'string' ? urlOrFileName : urlOrFileName?.name || '';
    const lower = str.split('?')[0].toLowerCase();
    if (IMAGE_EXT.some((ext) => lower.endsWith(ext))) return 'image';
    if (PDF_EXT.some((ext) => lower.endsWith(ext))) return 'pdf';
    return 'other';
};

const AssignmentFilePreviewModal = ({ open, onClose, url, fileName, mimeType }) => {
    const { t, i18n } = useTranslation();
    const direction = i18n.dir();
    const [zoom, setZoom] = useState(100);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [displayUrl, setDisplayUrl] = useState(url);

    useEffect(() => {
        setDisplayUrl(url);
        setZoom(100);
    }, [url]);

    const fileType = useCallback(() => {
        if (mimeType) {
            if (mimeType.startsWith('image/')) return 'image';
            if (mimeType === 'application/pdf') return 'pdf';
        }
        return getFileType(fileName || url);
    }, [mimeType, fileName, url])();

    const handleZoomIn = () => setZoom((z) => Math.min(z + 25, 300));
    const handleZoomOut = () => setZoom((z) => Math.max(z - 25, 25));
    const handleZoomChange = (_, v) => setZoom(v);
    const toggleFullscreen = () => setIsFullscreen((f) => !f);

    const handleDownload = () => {
        if (!displayUrl) return;
        const a = document.createElement('a');
        a.href = displayUrl;
        a.download = fileName || 'document';
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    if (!open) return null;

    const zoomValue = zoom / 100;
    const previewStyle = {
        width: '100%',
        height: isFullscreen ? '74vh' : '60vh',
        minHeight: 300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'auto',
        backgroundColor: '#2d2d2d',
        borderRadius: 'var(--xtram-radius-md, 10px)',
    };

    return (
        <XtramModal
            open={open}
            onClose={onClose}
            direction={direction}
            expanded
            title={`${t('DocumentPreview')}${fileName ? ` - ${fileName}` : ''}`}
            footer={(
                <XtramButton variant="secondary" onClick={onClose}>
                    {t('Close')}
                </XtramButton>
            )}
        >
            <div className={xtramStyles.toolbar}>
                <div className={xtramStyles.toolbarGroup}>
                    <Tooltip title={t('ZoomOut') || 'Zoom out'}>
                        <IconButton onClick={handleZoomOut} size="small" className={xtramStyles.closeIcon}>
                            <ZoomOutIcon />
                        </IconButton>
                    </Tooltip>
                    <Slider
                        value={zoom}
                        min={25}
                        max={300}
                        step={5}
                        onChange={handleZoomChange}
                        sx={{ width: 120 }}
                        size="small"
                        valueLabelDisplay="auto"
                        valueLabelFormat={(v) => `${v}%`}
                    />
                    <Tooltip title={t('ZoomIn') || 'Zoom in'}>
                        <IconButton onClick={handleZoomIn} size="small" className={xtramStyles.closeIcon}>
                            <ZoomInIcon />
                        </IconButton>
                    </Tooltip>
                </div>
                <div className={xtramStyles.toolbarGroup}>
                    <XtramButton variant="ghost" small active={isFullscreen} onClick={toggleFullscreen}>
                        {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                        {isFullscreen ? (t('ExitFullscreen') || 'Exit fullscreen') : (t('Fullscreen') || 'Fullscreen')}
                    </XtramButton>
                    <XtramButton variant="ghost" small onClick={handleDownload} disabled={!displayUrl}>
                        <DownloadIcon fontSize="small" />
                        {t('Download') || 'Download'}
                    </XtramButton>
                </div>
            </div>

            <div style={previewStyle}>
                {displayUrl && fileType === 'image' && (
                    <img
                        src={displayUrl}
                        alt={fileName || 'Preview'}
                        style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain',
                            transform: `scale(${zoomValue})`,
                            transformOrigin: 'center center',
                        }}
                    />
                )}
                {displayUrl && fileType === 'pdf' && (
                    <iframe
                        src={`${displayUrl}#toolbar=1&navpanes=1&scrollbar=1`}
                        title={fileName || 'PDF Preview'}
                        style={{
                            width: `${100 * zoomValue}%`,
                            height: '100%',
                            minHeight: 400,
                            border: 'none',
                            borderRadius: 4,
                        }}
                    />
                )}
                {displayUrl && fileType === 'other' && (
                    <div className={xtramStyles.emptyState} style={{ color: '#fff' }}>
                        <p>{t('PreviewNotAvailable') || 'Preview not available for this file type.'}</p>
                        <XtramButton variant="primary" onClick={handleDownload}>
                            <DownloadIcon fontSize="small" />
                            {t('Download') || 'Download'}
                        </XtramButton>
                    </div>
                )}
                {!displayUrl && (
                    <div className={xtramStyles.emptyState} style={{ color: '#fff' }}>
                        {t('NoPreview') || 'No file to preview'}
                    </div>
                )}
            </div>
        </XtramModal>
    );
};

export default AssignmentFilePreviewModal;
